import axios from 'axios';
import networkConstant from '../../constants/network.constant';
import { NetworkId } from '../../types/network.types';
import {
  BINANCE_SUBGRAPH_URL,
  BSC_LEGACY_STAKING_SUBGRAPH_URL,
  BSC_TESTNET_LEGACY_STAKING_SUBGRAPH_URL,
  ETH_LEGACY_STAKING_SUBGRAPH_URL,
  ITEMS_PER_QUERY,
  POLY_LEGACY_STAKING_SUBGRAPH_URL,
} from '../thegraph.staking.service';
import { StakingTransactionHistory, StakingTransactionHistoryLP } from './staking.types';

const KOVAN_STAKING_SUBGRAPHURL =
  'https://api.thegraph.com/subgraphs/name/mantradao/kovan-subgraph-staking-legacy';

export function getStakingGraphAddress(networkId: NetworkId) {
  switch (networkId) {
    case networkConstant.networkId.ethMainnet:
      return ETH_LEGACY_STAKING_SUBGRAPH_URL;
    case networkConstant.networkId.bscMainnet:
      return BSC_LEGACY_STAKING_SUBGRAPH_URL;
    case networkConstant.networkId.bscTestnet:
      return BSC_TESTNET_LEGACY_STAKING_SUBGRAPH_URL;
    case networkConstant.networkId.polyMainnet:
      return POLY_LEGACY_STAKING_SUBGRAPH_URL;
    case networkConstant.networkId.ethKovan:
      return KOVAN_STAKING_SUBGRAPHURL;
    default:
      return ETH_LEGACY_STAKING_SUBGRAPH_URL;
  }
}

export interface TransactionHistoryProps {
  contractAddress: string;
  networkId: NetworkId;
  walletAddress: string;
}

export const StakingSubgraphService = {
  getNakedStakingTransactionHistory: async function ({
    contractAddress,
    networkId,
    walletAddress,
  }: TransactionHistoryProps): Promise<StakingTransactionHistory> {
    const url = getStakingGraphAddress(networkId);
    const { data } = await axios.post(url, {
      query: `
          {
            stakingPools(where: {address: "${contractAddress}"}) {
              stakes(where:{address:"${walletAddress}"},  orderBy: timestamp, orderDirection: desc) {
                id
                stakedAmount
                timestamp
              }
              unstakes(where:{address:"${walletAddress}"}, orderBy: timestamp, orderDirection: desc) {
                id
                unstakedAmount
                timestamp
              }
              claims(where:{address:"${walletAddress}"}, orderBy: timestamp, orderDirection: desc) {
                id
                claimedAmount
                timestamp
              }
            }
            }
          `,
    });

    const response = data.data.stakingPools[0];

    return response;
  },
  getLpStakingHistory: async function ({
    contractAddress,
    networkId,
    walletAddress,
  }: TransactionHistoryProps): Promise<StakingTransactionHistoryLP> {
    const url = getStakingGraphAddress(networkId);
    const { data } = await axios.post(url, {
      query: `
          {
            stakingPoolLPs(where: {address: "${contractAddress}"}) {
              stakesLP(where:{address:"${walletAddress}"},  orderBy: timestamp, orderDirection: desc) {
                id
                stakedAmount
                timestamp
              }
              unstakesLP(where:{address:"${walletAddress}"}, orderBy: timestamp, orderDirection: desc) {
                id
                unstakedAmount
                timestamp
              }
              claims(where:{address:"${walletAddress}"}, orderBy: timestamp, orderDirection: desc) {
                id
                claimedAmount
                timestamp
              }
            }
            }
          `,
    });

    const response = data.data.stakingPoolLPs[0];

    return response;
  },

  getTransfersPastLogs: async (
    token: string,
    first: number,
    lastId: string,
    url: string = BINANCE_SUBGRAPH_URL,
  ) => {
    const { data: response } = await axios.post(`${url}`, {
      query: `
          {
            transfers(first: ${first}, orderBy: id, where: {tokenSymbol: "${token}", id_gt: "${lastId}"}) {
              from
              id
              to
              value
              blockNumber
              logIndex
              tokenSymbol
            }
          }
        `,
    });
    return response.data;
  },
  getPricesPastLogs: async (
    token: string,
    first: number,
    lastId: string,
    url: string = BINANCE_SUBGRAPH_URL,
  ) => {
    const { data: response } = await axios.post(`${url}`, {
      query: `
          {
            priceUpdates(first: ${first}, orderBy:id, where: {tokenSymbol: "${token}", id_gt: "${lastId}"}) {
              id
              mantissa
              exponentiation
              base
              blockNumber
              tokenSymbol
            }
          }
        `,
    });

    return response.data;
  },
};

export const getAllTransfersLogs = async (token: string, url?: string) => {
  const total: object[] = [];
  let currentId = '';
  await (async function recursiveGetdata() {
    const { transfers } = await StakingSubgraphService.getTransfersPastLogs(
      token,
      ITEMS_PER_QUERY,
      currentId,
      url,
    );
    currentId = transfers[transfers.length - 1].id;
    total.push(...transfers);

    if (transfers.length === ITEMS_PER_QUERY) {
      await recursiveGetdata();
    } else {
      return;
    }
  })();
  return total;
};
export const getAllPricesLogs = async (token: string, url?: string) => {
  const total: object[] = [];
  let currentId = '';
  await (async function recursiveGetdata() {
    const { priceUpdates } = await StakingSubgraphService.getPricesPastLogs(
      token,
      ITEMS_PER_QUERY,
      currentId,
      url,
    );
    currentId = priceUpdates[priceUpdates.length - 1].id;
    total.push(...priceUpdates);

    if (priceUpdates.length === ITEMS_PER_QUERY) {
      await recursiveGetdata();
    } else {
      return;
    }
  })();
  return total;
};
